/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Heart,
  Bed,
  Bath,
  Car,
  Maximize,
  Home,
  ArrowLeft,
} from "lucide-react";
import { propertiesApi, photoUrl } from "./api";
import { formatInr } from "../utils/inrFormat";
import "./PropertiesBrowse.css";

export default function SavedProperties() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await propertiesApi.mySaves();
      setItems(rows || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onUnsave = async (id) => {
    try {
      await propertiesApi.unsave(id);
      setItems((arr) => arr.filter((p) => p.id !== id));
    } catch (_) {
      load();
    }
  };

  return (
    <div className="vsp-br-shell">
      <button
        type="button"
        onClick={() => navigate("/properties")}
        className="vsp-br-back-link"
      >
        <ArrowLeft size={14} /> Back to all properties
      </button>

      <div className="vsp-br-head">
        <div>
          <h1>Saved properties</h1>
          <p className="vsp-br-sub">
            {items.length === 0
              ? "Tap the heart on any listing to save it for later."
              : `${items.length} listing${items.length === 1 ? "" : "s"} saved.`}
          </p>
        </div>
      </div>

      {error && <div className="vsp-br-error">{error}</div>}
      {loading && <div className="vsp-br-empty">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="vsp-br-empty-card">
          <Heart size={28} strokeWidth={1.4} />
          <h3>No saved listings yet</h3>
          <p>Hearts you tap on the browse or detail page show up here.</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="vsp-br-grid">
          {items.map((p) => (
            <SavedCard key={p.id} property={p} onUnsave={onUnsave} />
          ))}
        </div>
      )}
    </div>
  );
}

function SavedCard({ property, onUnsave }) {
  const price =
    property.type === "RENT"
      ? formatInr(property.monthly_rent_inr, { monthly: true })
      : formatInr(property.expected_price_inr);
  const villaNum = property.villa?.villa_number || "—";
  const bhk = `${property.bedrooms} BHK`;
  const furnLabel =
    property.furnishing === "FURNISHED"
      ? "Furnished"
      : property.furnishing === "SEMI"
      ? "Semi-Furnished"
      : "Unfurnished";

  const onHeart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onUnsave(property.id);
  };

  return (
    <Link to={`/properties/${property.id}`} className="vsp-br-card">
      <div className="vsp-br-card-img">
        {property.primary_photo_url ? (
          <img src={photoUrl(property.primary_photo_url)} alt={villaNum} />
        ) : (
          <div className="vsp-br-card-placeholder">
            <Home size={32} strokeWidth={1.2} />
          </div>
        )}
        <span
          className={`vsp-br-card-type ${
            property.type === "RENT" ? "rent" : "sale"
          }`}
        >
          {property.type === "RENT" ? "For Rent" : "For Sale"}
        </span>
        <button
          type="button"
          className="vsp-br-card-heart active"
          onClick={onHeart}
          aria-label="Remove from saved"
          title="Remove from saved"
        >
          <Heart size={16} fill="#dc2626" />
        </button>
      </div>

      <div className="vsp-br-card-body">
        <h3>{villaNum}</h3>
        <div className="vsp-br-card-bhk">{bhk} · Independent Villa</div>
        <div className="vsp-br-card-price">{price}</div>
        {property.type === "RENT" && property.monthly_maintenance_inr > 0 && (
          <div className="vsp-br-card-extras">
            + {formatInr(property.monthly_maintenance_inr, { monthly: true })}{" "}
            maintenance
          </div>
        )}
        <div className="vsp-br-card-icons">
          <span><Bed size={13} /> {property.bedrooms}</span>
          <span><Bath size={13} /> {property.bathrooms}</span>
          <span><Car size={13} /> {property.parking}</span>
          <span><Maximize size={13} /> {property.built_up_area_sqft.toLocaleString("en-IN")} sqft</span>
        </div>
        <div className="vsp-br-card-foot">
          <span className="vsp-br-furn">{furnLabel}</span>
        </div>
      </div>
    </Link>
  );
}
